<script setup lang="ts">
// Card de plano (mobile) da tela única de assinatura.
// Base: AssinePlanPeriodMobile.vue. Diferenças principais:
//  - O `disabled` de cada rádio vem de `isPeriodEnabled()` (composable de regras),
//    substituindo a lógica antiga baseada só no flag `upgrade` do backend.
//  - "Use seu saldo" + valor (sem "/mês") aparece nas células de upgrade/saldo,
//    conforme `describeCell()`.
//  - Benefícios centralizados abaixo do card (getMobileBenefits/getInheritsFromLabel).
//  - No mobile a escolha é salva ao AVANÇAR (botão), não ao selecionar o rádio.
import translaters from "@/tradutors/assine";
import { formatMoney } from "@/shared/utils/money";
import { comHorasDaLive } from "@/utils/horasDaLive";
import CarimboMaisEscolhidoPt from "assets/img/carimbo-mais-escolhido-pt.png";
import CarimboMaisEscolhidoEn from "assets/img/carimbo-mais-escolhido-en.png";
import GoldBg from "assets/img/card-assine-gold-background-.png";

const storePlan = planStore();
const storeAssineChoice = assineChoiceStore();
const storeUser = userStore();
const route = useRoute();
const router = useRouter();

const isActiveUser = computed(() => storeUser.isActiveUser);
const isLoggedIn = computed(() => storeUser.isLoggedIn);
const dailylimit = computed(() => storePlan.effectiveDownloadLimit);
const reactivePlans = computed(() => storePlan.plansType);
const reactiveLocale = computed(() => route.params.language);

const props = defineProps({
  // Id do tipo de plano (Basic/Plus/Gold) deste card.
  planType: {
    type: Number,
    required: true,
  },
  // Pedido atual do usuário (repassado pelo wrapper; pode ser {} até a API responder).
  order: {
    type: [Object, Boolean],
    required: false,
    default: () => ({}),
  },
  // Exibir os benefícios por card + rodapé abaixo do card. Mobile usa o padrão
  // (true); o desktop reusa o card numa fileira e passa false, pois a comparação
  // de benefícios fica na tabela do AssinePlansGridDesktop.
  showBenefits: {
    type: Boolean,
    required: false,
    default: true,
  },
});

// Regras centrais (sequência linear + estados Gold). Mesma fonte usada no desktop.
const { describeCell, isPeriodEnabled, findPlan } = useAssinePlanRules({
  order: computed(() => props.order),
  isLoggedIn,
  isActiveUser,
  saldoUpgrade: computed(() => storePlan.saldoUpgrade),
});

// Sufixo de periodicidade exibido após o texto de créditos do Plus.
function creditsPeriodSuffix(months: number): string {
  if (months === 12) return (translate.value as any).textCreditsPeriod12;
  if (months === 3) return (translate.value as any).textCreditsPeriod3;
  return (translate.value as any).textCreditsPeriod1;
}

// Tipo de plano selecionado por este card (a partir do plan_type_id).
const plansSelected = computed(() =>
  reactivePlans.value.filter((plan) => plan.plan_type_id == props.planType)
);

const planTypeName = computed(
  () => plansSelected.value[0]?.plan_type_name ?? ""
);

// Mobile: ordena períodos ascendente (Mensal → Trimestral → Anual).
const plansAsc = computed(() => {
  const plans = plansSelected.value[0]?.plans ?? [];
  return [...plans].sort((a: any, b: any) => a.months - b.months);
});

function goldPlan() {
  return planTypeName.value === "Gold";
}

// Texto de parcelamento de cada período, vindo do servidor: "até 12X" (Gold e
// Plus liberados na regra nova; o Gold na regra de antes).
const { carregar: carregarParcelas, rotulo: rotuloDoServidor } =
  useParcelamento();

function carregarTodasAsParcelas() {
  return Promise.all(
    (plansAsc.value as any[])
      .filter((p: any) => Number(p?.months) > 1 && p?.id)
      .map((p: any) => carregarParcelas(p.id, String(reactiveLocale.value)))
  );
}

watch(
  () => [plansAsc.value.map((p: any) => p.id).join(","), reactiveLocale.value],
  () => carregarTodasAsParcelas(),
  { immediate: true }
);

// No servidor, a página só sai com as opções carregadas: o card do navegador
// nasce igual (sem o valor por mês trocado por "até 12X" depois da hidratação,
// que não atualiza as classes).
onServerPrefetch(() => carregarTodasAsParcelas());

function rotuloParcelas(item: any): string | null {
  if (!item?.id || Number(item?.months) <= 1) return null;
  return rotuloDoServidor(item.id, String(reactiveLocale.value), goldPlan());
}

const translate = computed(() => {
  if (reactiveLocale.value === "pt-br") {
    return translaters.portugues;
  }
  return translaters.ingles;
});

// Posiciona o rádio inicial (mesmo padrão do AssinePlanPeriodMobile.vue):
// Gold → Anual; code_share/voucher → primeiro item; senão → Trimestral.
// Respeita o `disabled` calculado pelas regras (só marca rádio habilitado).
onMounted(() => {
  setTimeout(() => {
    const radioButtons = document.querySelectorAll<HTMLInputElement>(
      `input[name="plansOptionV2${props.planType}"]`
    );
    if (!radioButtons.length) return;
    radioButtons.forEach((radio) => {
      radio.checked = false;
    });

    const anualIdx = plansAsc.value.findIndex((p: any) => p.months === 12);
    const trimestralIdx = plansAsc.value.findIndex((p: any) => p.months === 3);

    const hasVoucher = plansAsc.value.some((p: any) => p.value_voucher);
    const hasCodeShare =
      route.query.code_share &&
      plansAsc.value.some((p: any) => p.plan_type_name === "Basic");

    let idx: number;
    if (goldPlan()) {
      idx = anualIdx;
    } else if (hasCodeShare || hasVoucher) {
      idx = 0;
    } else {
      idx = trimestralIdx;
    }
    if (idx < 0) return;
    const target = radioButtons[idx];
    if (target) target.checked = !target.disabled;
  }, 500);
});

function eventGTMSelectPlan() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "select_plan",
    select_plan: {
      tipo:
        storeAssineChoice.choiceType !== "credits" ? "assinatura" : "credito",
      acionador: route.query.acionador ?? "link direto",
      currency: reactiveLocale.value === "pt-br" ? "BRL" : "USD",
      value: choice.value.choiceValue ?? 0,
      plano: choice.value.choiceTitle,
    },
  });
}

const choice = ref({
  choiceTitle: "Não definido",
  choiceValue: 0,
});

// Salva a escolha (set choice + sessionStorage) e AVANÇA (GTM + redirect).
// Mantém o mesmo fluxo do AssinePlanPeriodMobile.vue::saveChoicePeriod.
function saveChoicePeriod() {
  const selector = `input[name="plansOptionV2${props.planType}"]:checked`;
  const checked = document.querySelector<HTMLInputElement>(selector);
  if (!checked) {
    alert(
      reactiveLocale.value == "pt-br" ? "Selecione um período " : "Select a period"
    );
    return false;
  }
  const choiceId = parseInt(checked.value);
  const choiceType = checked.getAttribute("alt");

  if (!choiceId) {
    alert(
      reactiveLocale.value == "pt-br" ? "Selecione um período " : "Select a period"
    );
    return false;
  }

  storeAssineChoice.setAssineChoicePlanType({
    choicePlanTypeId: props.planType,
    // Nome real do tipo de plano ("Gold"/"Plus"/"Basic"); o `choiceType` do
    // rádio ("plans"/"plansUnique") continua indo só para setAssineChoice.
    choicePlanTypeName: planTypeName.value,
  });
  storeAssineChoice.setAssineChoice({ choiceId, choiceType: choiceType ?? "plans" });

  // Monta título/valor do GTM (idêntico ao componente base).
  reactivePlans.value.map((plan) => {
    plan.plans?.map((item) => {
      if (item.id == choiceId) {
        choice.value.choiceTitle =
          (reactiveLocale.value == "pt-br" ? "Plano " : "Plan ") +
          item.period +
          ` - ${plan.plan_type_name}${
            item.value_voucher && +item.value_voucher > 0 ? " - Plano Unico" : ""
          }`;
        choice.value.choiceValue =
          +item.discount > 0 &&
          item.discount_value &&
          item.discount_value != +item.value
            ? +item.discount_value
            : item.value_voucher && +item.value_voucher > 0
            ? +item.value_voucher
            : +item.value;
      }
    });
  });

  eventGTMSelectPlan();

  if (isLoggedIn.value) {
    redirect("language-assine-checkout");
  } else {
    redirect("language-assine-account");
  }
}

function redirect(name: string) {
  router.push({
    name,
    params: {
      language: reactiveLocale.value,
    },
    query: {
      acionador: route.query.acionador,
      ...route.query,
    },
  });
}

// Marca o rádio ao clicar na linha inteira (só quando o período está habilitado).
function clickRadio(event: MouseEvent, item: any) {
  if (!isPeriodEnabled(planTypeName.value, item.months)) return;
  const input = (event.currentTarget as HTMLElement).querySelector(
    'input[type="radio"]'
  ) as HTMLInputElement;
  if (input && !input.disabled) {
    input.checked = true;
  }
}

// Açúcar de regras por célula: descreve o item (preço/upgrade/saldo) deste período.
function cellOf(item: any) {
  const plan = findPlan(planTypeName.value, item.months) ?? item;
  return describeCell({ typeName: planTypeName.value, months: item.months, plan });
}

// "Use seu saldo" deve aparecer? (upgrade/saldo do usuário ativo)
function showUseBalance(item: any): boolean {
  return cellOf(item).showUseBalance;
}

// Valor formatado da célula de upgrade/saldo (centavos sempre, sem "/mês").
function balanceValue(item: any): string {
  return formatMoney(cellOf(item).display.value, { alwaysCents: true });
}

// Preço (centavos sempre) da célula de preço, por mês.
function monthlyPriceOf(item: any): string {
  return formatMoney(cellOf(item).display.monthly, { alwaysCents: true });
}

// Preço total/à vista (centavos sempre).
function totalPriceOf(item: any): string {
  return formatMoney(cellOf(item).display.total, { alwaysCents: true });
}

function isPriceCell(item: any): boolean {
  return cellOf(item).display.mode === "price";
}

function isFreeCell(item: any): boolean {
  return item?.code_share === true || cellOf(item).display.mode === "free";
}

// Período BLOQUEADO para upgrade (assinante ativo): existe mas não é
// selecionável (disabledCell do composable). Mesmo conceito do `cell-disabled`
// do desktop: fundo cinza + preço borrado/centralizado.
function isBlockedCell(item: any): boolean {
  const cell = cellOf(item);
  return cell.exists && !cell.enabled;
}

// Preço da célula bloqueada calculado localmente (o composable não carrega
// valor em disabledCell, igual ao desktop): usa discount_value quando houver
// promo real, senão value.
function disabledTotalNumber(item: any): number {
  const value = Number(item?.value) || 0;
  const discount = Number(item?.discount) || 0;
  const discountValue = Number(item?.discount_value) || 0;
  return discount > 0 && discountValue > 0 && discountValue !== value
    ? discountValue
    : value;
}

// Créditos do Plus: só reduz proporcional ao desconto quando há PROMOÇÃO DE
// CUPOM (preço de/por real). Sem cupom, item.discount é só a economia anual vs
// mensal — mantém a base do período (40/8/2).
function creditsForPlus(item: any): number {
  const base = Number(item?.credits) || 0;
  const value = Number(item?.value) || 0;
  const discount = Number(item?.discount) || 0;
  const discountValue = Number(item?.discount_value) || 0;
  const hasCouponPromo = discount > 0 && discountValue > 0 && discountValue !== value;
  return hasCouponPromo ? base - parseInt(String(base * (discount / 100))) : base;
}
function disabledMonthlyOf(item: any): string {
  const m = Number(item?.months) || 1;
  return formatMoney(disabledTotalNumber(item) / m, { alwaysCents: true });
}
function disabledTotalOf(item: any): string {
  return formatMoney(disabledTotalNumber(item), { alwaysCents: true });
}

function currencyPrefix(): string {
  // PT-BR: sem prefixo "R$" (mostra só o valor). EN: mantém "$".
  return reactiveLocale.value === "pt-br" ? "" : "$";
}

// ----- Promoção do visitante: exibição "de / por" -----
// Há promoção quando o item tem preço promocional real: discount > 0 e
// discount_value presente e diferente de value (caso de cupom). "de" = value
// (cheio), "por" = discount_value (já calculado pelo backend).
function itemHasPromo(item: any): boolean {
  if (item?.value_voucher) return false;
  const value = Number(item?.value) || 0;
  const discount = Number(item?.discount) || 0;
  const discountValue = Number(item?.discount_value) || 0;
  return discount > 0 && discountValue > 0 && discountValue !== value;
}

// Card de UPGRADE com promoção válida para usuário ATIVO: exibe o preço cheio
// riscado ("de") acima do valor a pagar ("por"). O backend só monta o
// upgrade_value sobre o discount_value quando o link da promoção permite
// usuário ativo (allow_active_user); nesse caso o item chega com
// discount/discount_value preenchidos (nas promoções que NÃO permitem ativo o
// desconto nem é calculado para ele, então discount_value fica 0). O modo
// "upgrade" garante que o valor exibido veio do backend (upgrade_value) — no
// fallback "balance-remainder" o desconto não incide, e mostrar o "de/por" ali
// anunciaria um abatimento que o valor não tem.
function showUpgradeFullPrice(item: any): boolean {
  return (
    showUseBalance(item) &&
    itemHasPromo(item) &&
    cellOf(item).display.mode === "upgrade"
  );
}

// Esconde o HR entre períodos quando o item atual OU o próximo tem fundo de
// promoção (Plus/Basic): o realce peach que sangra até a borda + o espaço em
// branco de 10px já separam visualmente os períodos.
function hideHrForPromo(item: any, index: number): boolean {
  const cur = itemHasPromo(item) && !goldPlan();
  const next = (plansAsc.value as any[])[index + 1];
  const nextPromo = !!next && itemHasPromo(next) && !goldPlan();
  return cur || nextPromo;
}
// Voucher (plano único): value_voucher como preço do plano específico (ex.: Plus Mensal).
function voucherValueOf(item: any): string {
  return formatMoney(item?.value_voucher ?? 0, { alwaysCents: true });
}
function fullMonthlyOf(item: any): string {
  const m = Number(item?.months) || 1;
  return formatMoney((Number(item?.value) || 0) / m, { alwaysCents: true });
}
function fullTotalOf(item: any): string {
  return formatMoney(Number(item?.value) || 0, { alwaysCents: true });
}
function promoMonthlyOf(item: any): string {
  const m = Number(item?.months) || 1;
  return formatMoney((Number(item?.discount_value) || 0) / m, {
    alwaysCents: true,
  });
}
function promoTotalOf(item: any): string {
  return formatMoney(Number(item?.discount_value) || 0, { alwaysCents: true });
}

// Percentual de desconto "padrão" do período (visitante sem cupom), exibido
// abaixo do NOME do período em Tri/Anual (não-Gold). Não vale p/ voucher/presente.
function itemDiscountPct(item: any): number {
  if (item?.code_share || item?.value_voucher) return 0;
  return Number(item?.discount) || 0;
}

// Layout EMPILHADO (nome centralizado na linha 1 + valor na linha 2): promoção
// "de/por" (cupom) e upgrade ("Use seu saldo"). Demais casos usam o lado-a-lado.
function isStacked(item: any): boolean {
  return itemHasPromo(item) || showUseBalance(item);
}

// Parcelamento junto ao valor ("ou até 12X"), no lugar do valor por mês: o Gold
// da regra de antes e, na regra nova, os planos liberados (Gold e Plus).
function showInstallments(item: any): boolean {
  return !!rotuloParcelas(item);
}

// No Basic, só o Mensal mostra o valor por mês; Trimestral e Anual ficam com o
// total à vista.
function showMonthlyPrice(item: any): boolean {
  return !(planTypeName.value === "Basic" && Number(item?.months) > 1);
}

// Sem o valor por mês abaixo dele, o total à vista é o preço em destaque.
function totalEmDestaque(item: any): boolean {
  return showInstallments(item) || !showMonthlyPrice(item);
}

// Percentual da promoção (cupom) exibido abaixo do NOME do plano (título do card).
// Usa o desconto do primeiro período (asc) com promoção real; 0 esconde.
const planHeaderPct = computed(() => {
  for (const item of plansAsc.value as any[]) {
    if (itemHasPromo(item)) return Number(item.discount) || 0;
  }
  return 0;
});

// Selo "Mais Escolhido" só no Plus.
const mostChosen = computed(() => planTypeName.value === "Plus");

// Algum período habilitado neste card? (define se o botão de avançar aparece ativo)
const hasAnyEnabled = computed(() =>
  plansAsc.value.some((item: any) =>
    isPeriodEnabled(planTypeName.value, item.months)
  )
);

// Benefícios mobile centralizados (mesmo helper do AssinePlanCard.vue), com as horas
// de live do admin ("Tempo de acesso") no lugar de {horasPlus}/{horasGold}.
const horasDaLive = useHorasDaLive();
function getMobileBenefits(name: string): string[] {
  const map = (translate.value as any).planBenefitsMobile as Record<
    string,
    string[]
  >;
  return (map?.[name] ?? []).map((texto) => comHorasDaLive(texto, horasDaLive.value));
}

function getInheritsFromLabel(name: string): string | null {
  if (name === "Plus") return (translate.value as any).planBenefitsInheritBasic;
  if (name === "Gold") return (translate.value as any).planBenefitsInheritPlus;
  return null;
}

const svgCheck =
  '<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="15px" height="15px" viewBox="0,0,256,256"style="fill:#40C057;"><g fill="#40c057" fill-rule="nonzero" stroke="#40c057" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" stroke-dasharray="" stroke-dashoffset="0" font-family="none" font-weight="none" font-size="none" text-anchor="none" style="mix-blend-mode: normal"><g transform="scale(5.12,5.12)"><path d="M41.9375,8.625c-0.66406,0.02344 -1.27344,0.375 -1.625,0.9375l-18.8125,28.78125l-12.1875,-10.53125c-0.52344,-0.54297 -1.30859,-0.74609 -2.03125,-0.51953c-0.71875,0.22266 -1.25391,0.83203 -1.37891,1.57422c-0.125,0.74609 0.17578,1.49609 0.78516,1.94531l13.9375,12.0625c0.4375,0.37109 1.01563,0.53516 1.58203,0.45313c0.57031,-0.08594 1.07422,-0.41016 1.38672,-0.89062l20.09375,-30.6875c0.42969,-0.62891 0.46484,-1.44141 0.09375,-2.10547c-0.37109,-0.66016 -1.08594,-1.05469 -1.84375,-1.01953z"></path></g></g></svg>';
</script>

<template>
  <div class="container" :id="`plan-period-v2-${props.planType}`">
    <div
      class="card"
      :class="{ 'card-gold': goldPlan() }"
      :style="goldPlan() ? { '--gold-bg': 'url(' + GoldBg + ')' } : {}"
    >
      <div class="banner-gold py-2" v-if="goldPlan()">
        {{ reactiveLocale == "pt-br" ? "Nosso plano para" : "Our plan for the" }}
        <b>{{ reactiveLocale == "pt-br" ? "Homem Raiz" : "Old School Man" }}</b>
      </div>

      <!-- Selo "Mais Escolhido" só no Plus. -->
      <img
        v-if="mostChosen"
        :src="reactiveLocale == 'pt-br' ? CarimboMaisEscolhidoPt : CarimboMaisEscolhidoEn"
        class="carimbo-mais-escolhido"
        alt=""
      />

      <div class="card-body">
        <div
          class="plantype-name d-flex justify-content-center align-items-center mt-3"
        >
          {{ planTypeName }}
        </div>
        <!-- Percentual da promoção (cupom) abaixo do nome do plano — só no Gold.
             Plus/Basic mostram o % acima do nome de cada período em promoção. -->
        <div
          v-if="planHeaderPct > 0 && goldPlan()"
          class="plantype-discount-pct d-flex justify-content-center align-items-center"
        >
          {{ planHeaderPct }}% {{ translate.labelDiscont }}
        </div>
        <!-- Linha divisória sob o título: mantida em Plus/Basic, oculta no Gold. -->
        <hr v-if="!goldPlan()" class="hr-desire" />

        <div v-for="(item, index) in plansAsc" :key="index">
          <div class="card-item">
            <!-- Selos de voucher / presente (responsabilidade do componente). -->
            <div
              v-if="item?.code_share == true"
              class="card-discount-voucher d-flex justify-content-center align-items-center"
            >
              {{
                reactiveLocale == "pt-br"
                  ? "Presente do seu amigo!"
                  : "Gift from your friend!"
              }}
            </div>
            <div
              v-else-if="item.value_voucher"
              class="card-discount-voucher d-flex justify-content-center align-items-center"
            >
              {{ translate.Promotionexclusive }}
            </div>

            <div
              class="card-body-item justify-content-center py-2"
              :class="{
                blocked: isBlockedCell(item),
                'promo-highlight': itemHasPromo(item) && !goldPlan() && !showUseBalance(item),
              }"
              @click="clickRadio($event, item)"
            >
              <!-- LAYOUT BLOQUEADO (upgrade): mesmo conceito do desktop — fundo
                   cinza + preço borrado e centralizado. Sem rádio visível (input
                   oculto mantido só para alinhar os índices do onMounted). -->
              <template v-if="isBlockedCell(item)">
                <input
                  class="form-check-input blocked-radio-hidden"
                  type="radio"
                  :name="`plansOptionV2${props.planType}`"
                  :value="item.id"
                  alt="plans"
                  disabled
                />
                <div class="plan-period plan-blur text-center mb-2">{{ item.period }}</div>
                <div class="plan-blocked text-center">
                  <div class="plan-price-month plan-blur" v-if="showMonthlyPrice(item)">
                    {{ currencyPrefix() }} {{ disabledMonthlyOf(item) }}
                    <span class="plan-permonth">{{ translate.perMonth }}</span>
                  </div>
                  <hr v-if="item.months > 1 && showMonthlyPrice(item)" class="hr-blur" />
                  <div class="plan-price-total plan-blur" v-if="item.months > 1">
                    {{ currencyPrefix() }} {{ disabledTotalOf(item) }}
                    {{ translate.totalValue }}
                  </div>
                </div>
              </template>

              <!-- LAYOUT GOLD PROMOÇÃO: empilhado vertical próprio (mockup).
                   "de" (total riscado em vermelho, sem "à vista") acima do "por"
                   (total à vista + valor mensal). -->
              <template
                v-else-if="goldPlan() && itemHasPromo(item) && !showUseBalance(item)"
              >
                <div class="gold-cell gold-promo text-center">
                  <!-- Linha 1: rádio + nome do período (Anual). -->
                  <div class="gold-line-period">
                    <input
                      class="form-check-input"
                      type="radio"
                      :name="`plansOptionV2${props.planType}`"
                      :value="item.id"
                      alt="plans"
                      :disabled="!isPeriodEnabled(planTypeName, item.months)"
                    />
                    <span class="gold-period">{{ item.period }}</span>
                  </div>
                  <!-- Linha 2: "de" + valor total riscado (sem "à vista"). -->
                  <div class="gold-de-row">
                    <span class="gold-de-label">{{
                      reactiveLocale == "pt-br" ? "de" : "of"
                    }}</span>
                    <span class="gold-de-value plan-de-strike">
                      {{ currencyPrefix() }} {{ fullTotalOf(item) }}
                    </span>
                  </div>
                  <!-- Linha 3: "por" + total à vista (sublinha 1) e mensal (sublinha 2). -->
                  <div class="gold-por-row">
                    <span class="gold-por-label">{{
                      reactiveLocale == "pt-br" ? "por" : "only"
                    }}</span>
                    <div class="gold-por-stack">
                      <div class="gold-total">
                        {{ currencyPrefix() }} {{ promoTotalOf(item) }}
                        <template v-if="!showInstallments(item)">{{ translate.totalValue }}</template>
                      </div>
                      <!-- Com parcelamento, o card não mostra o valor por mês. -->
                      <div class="gold-month" v-if="!showInstallments(item)">
                        {{ currencyPrefix() }} {{ promoMonthlyOf(item) }}
                        <span class="plan-permonth">{{ translate.perMonth }}</span>
                      </div>
                    </div>
                  </div>
                  <!-- Linha 4: "à vista ou até 12X", abaixo do valor. -->
                  <div class="gold-installments" v-if="showInstallments(item)">
                    <span class="gold-total">{{ translate.totalValue }} ou</span>
                    {{ rotuloParcelas(item) }}
                  </div>
                </div>
              </template>

              <!-- LAYOUT EMPILHADO: promoção "de/por" (cupom) e upgrade ("Use seu
                   saldo"). Nome centralizado na linha 1; valor na linha 2. -->
              <template v-else-if="isStacked(item)">
                <!-- GOLD em upgrade/saldo: rádio AO LADO do nome (como o visitante),
                     centralizado; abaixo, "Use seu saldo" + valor. -->
                <div
                  v-if="showUseBalance(item) && goldPlan()"
                  class="gold-cell text-center"
                >
                  <div class="gold-line-period">
                    <input
                      class="form-check-input"
                      type="radio"
                      :name="`plansOptionV2${props.planType}`"
                      :value="item.id"
                      alt="plans"
                      :disabled="!isPeriodEnabled(planTypeName, item.months)"
                    />
                    <span class="gold-period">{{ item.period }}</span>
                  </div>
                  <div class="plan-balance text-center">
                    <div class="use-balance-label">{{ translate.useYourBalance }}</div>
                    <div class="use-balance-label-plus">+</div>
                    <!-- Promoção válida para o usuário ativo: "de" + preço cheio
                         riscado em vermelho, acima do "por" + valor a pagar. -->
                    <div v-if="showUpgradeFullPrice(item)" class="balance-de-row">
                      <span class="plan-depor-label">{{
                        reactiveLocale == "pt-br" ? "de" : "of"
                      }}</span>
                      <span class="balance-de-value plan-de-strike">
                        {{ currencyPrefix() }} {{ fullTotalOf(item) }}
                      </span>
                    </div>
                    <div class="balance-por-row">
                      <span v-if="showUpgradeFullPrice(item)" class="plan-depor-label">{{
                        reactiveLocale == "pt-br" ? "por" : "only"
                      }}</span>
                      <span class="plan-balance-value">
                        {{ currencyPrefix() }} {{ balanceValue(item) }}
                      </span>
                    </div>
                    <span v-if="showInstallments(item)" class="sem-juros">
                      em {{ rotuloParcelas(item) }}
                    </span>
                  </div>
                </div>

                <!-- Plus/Basic (upgrade OU promoção): rádio na col-1 + conteúdo na col-11. -->
                <div
                  v-else
                  class="row d-flex justify-content-center align-items-center"
                >
                  <div class="col-1">
                    <input
                      class="form-check-input"
                      type="radio"
                      :name="`plansOptionV2${props.planType}`"
                      :value="item.id"
                      :alt="`${item.value_voucher ? 'plansUnique' : 'plans'}`"
                      :disabled="!isPeriodEnabled(planTypeName, item.months)"
                    />
                  </div>
                  <div class="col-11">
                    <!-- % de desconto acima do nome do período (Plus/Basic em
                         promoção). Também aparece no modo upgrade ("Use seu
                         saldo") quando a promoção vale para o usuário ativo —
                         o Gold mostra o mesmo percentual abaixo do nome do
                         plano (plantype-discount-pct). -->
                    <div
                      v-if="itemHasPromo(item) && !goldPlan()"
                      class="period-promo-pct text-center"
                    >
                      {{ item.discount }}% {{ translate.labelDiscont }}
                    </div>

                    <!-- UPGRADE ("Use seu saldo"): nome centralizado + valor. -->
                    <template v-if="showUseBalance(item)">
                      <div class="plan-period text-center mb-2">{{ item.period }}</div>
                      <div class="plan-balance text-center">
                        <div class="use-balance-label">{{ translate.useYourBalance }}</div>
                        <div class="use-balance-label-plus">+</div>
                        <!-- Promoção válida para o usuário ativo: "de" + preço cheio
                             riscado em vermelho, acima do "por" + valor a pagar. -->
                        <div v-if="showUpgradeFullPrice(item)" class="balance-de-row">
                          <span class="plan-depor-label">{{
                            reactiveLocale == "pt-br" ? "de" : "of"
                          }}</span>
                          <span class="balance-de-value plan-de-strike">
                            {{ currencyPrefix() }} {{ fullTotalOf(item) }}
                          </span>
                        </div>
                        <div class="balance-por-row">
                          <span
                            v-if="showUpgradeFullPrice(item)"
                            class="plan-depor-label"
                            >{{ reactiveLocale == "pt-br" ? "por" : "only" }}</span
                          >
                          <span class="plan-balance-value">
                            {{ currencyPrefix() }} {{ balanceValue(item) }}
                          </span>
                        </div>
                        <span v-if="showInstallments(item)" class="sem-juros">
                          em {{ rotuloParcelas(item) }}
                        </span>
                      </div>
                    </template>

                    <!-- PROMOÇÃO Plus/Basic — 4 colunas (radio na col-1, fora):
                         col2 = nome + "de" valor riscado; col3 = "por";
                         col4 = valor à vista + valor mensal. -->
                    <div
                      v-else
                      class="promo-period-row"
                      :class="{ 'promo-period-row--single': item.months === 1 }"
                    >
                      <div class="promo-col-name">
                        <div class="plan-period">{{ item.period }}</div>
                        <div class="promo-de">
                          <span class="plan-depor-label">{{
                            reactiveLocale == "pt-br" ? "de" : "of"
                          }}</span>
                          <span class="plan-price-total plan-de-strike">
                            {{ currencyPrefix() }} {{ fullTotalOf(item) }}
                          </span>
                        </div>
                      </div>
                      <div class="promo-col-por">
                        <span class="plan-depor-label">{{
                          reactiveLocale == "pt-br" ? "por" : "only"
                        }}</span>
                      </div>
                      <div class="promo-col-value">
                        <div
                          class="plan-price-total"
                          :class="{ 'plan-price-total--destaque': totalEmDestaque(item) }"
                          v-if="item.months > 1"
                        >
                          <span class="plan-price-valor"
                            >{{ currencyPrefix() }} {{ promoTotalOf(item) }}</span
                          >
                          {{ translate.totalValue }}
                        </div>
                        <!-- Com parcelamento, "ou até 3X" no lugar do valor por mês. -->
                        <div class="sem-juros sem-juros--parcelas" v-if="showInstallments(item)">
                          ou {{ rotuloParcelas(item) }}
                        </div>
                        <div class="plan-price-month" v-else-if="showMonthlyPrice(item)">
                          {{ currencyPrefix() }} {{ promoMonthlyOf(item) }}
                          <span class="plan-permonth">{{ translate.perMonth }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <!-- LAYOUT GOLD (visitante/não-ativo, sem promoção): tudo
                   centralizado e empilhado, com fontes maiores que os outros
                   cards. Promo de cupom/saldo já caíram no isStacked acima. -->
              <template
                v-else-if="goldPlan() && isPriceCell(item) && !item.value_voucher"
              >
                <div class="gold-cell text-center">
                  <!-- Linha 1: rádio + nome do período (fonte maior). -->
                  <div class="gold-line-period">
                    <input
                      class="form-check-input"
                      type="radio"
                      :name="`plansOptionV2${props.planType}`"
                      :value="item.id"
                      alt="plans"
                      :disabled="!isPeriodEnabled(planTypeName, item.months)"
                    />
                    <span class="gold-period">{{ item.period }}</span>
                  </div>
                  <!-- Linha 2: valor por mês (fonte maior). Com parcelamento,
                       o card mostra só "à vista ou até 12X", sem o valor por mês. -->
                  <div class="gold-month" v-if="!showInstallments(item)">
                    {{ currencyPrefix() }} {{ monthlyPriceOf(item) }}
                    <span class="plan-permonth">{{ translate.perMonth }}</span>
                  </div>
                  <!-- Linha 3: hr. -->
                  <hr v-if="item.months > 1 && !showInstallments(item)" class="gold-hr" />
                  <!-- Linha 4: total à vista (fonte maior). -->
                  <!-- Com parcelamento, o à vista vira o valor em destaque. -->
                  <div
                    class="gold-total"
                    :class="{ 'gold-total--destaque': showInstallments(item) }"
                    v-if="item.months > 1"
                  >
                    <span class="gold-total-valor"
                      >{{ currencyPrefix() }} {{ totalPriceOf(item) }}</span
                    >
                    <template v-if="!showInstallments(item)">{{ translate.totalValue }}</template>
                  </div>
                  <!-- Linha 5: "à vista ou até 12X", abaixo do valor. -->
                  <div class="gold-installments" v-if="showInstallments(item)">
                    <span class="gold-total">{{ translate.totalValue }} ou</span>
                    {{ rotuloParcelas(item) }}
                  </div>
                </div>
              </template>

              <!-- LAYOUT LADO A LADO: visitante (preço normal), voucher e GRÁTIS.
                   Nome (+ % desconto em Tri/Anual) à esquerda; valor à direita. -->
              <template v-else>
                <div class="row d-flex justify-content-center align-items-center">
                  <div class="col-1">
                    <input
                      class="form-check-input"
                      type="radio"
                      :name="`plansOptionV2${props.planType}`"
                      :value="item.id"
                      :alt="`${item.value_voucher ? 'plansUnique' : 'plans'}`"
                      :disabled="!isPeriodEnabled(planTypeName, item.months)"
                    />
                  </div>
                  <div class="col-11 justify-content-center align-items-center">
                    <div class="row align-items-center">
                      <div :class="goldPlan() ? 'col-4' : 'col-6'">
                        <div class="plan-period ps-2">{{ item.period }}</div>
                        <!-- % de desconto do período (Tri/Anual não-Gold), abaixo do nome. -->
                        <div
                          class="plan-discount-pct ps-2"
                          v-if="item.months > 1 && !goldPlan() && itemDiscountPct(item) > 0"
                        >
                          {{ itemDiscountPct(item) }}% {{ translate.labelDiscont }}
                        </div>
                      </div>
                      <div :class="goldPlan() ? 'col-8 ps-3' : 'col-6 ps-3'">
                        <!-- Célula GRÁTIS (code_share). -->
                        <div v-if="isFreeCell(item)" class="plan-free">
                          {{ reactiveLocale == "pt-br" ? "GRÁTIS" : "FREE" }}
                        </div>
                        <!-- Voucher (plano único): value_voucher como preço. -->
                        <div v-else-if="item.value_voucher" class="plan-price">
                          <div class="plan-price-month">
                            {{ currencyPrefix() }} {{ voucherValueOf(item) }}
                            <span class="plan-permonth">{{
                              translate.perMonth
                            }}</span>
                          </div>
                        </div>
                        <!-- Preço normal: "à vista" em cima (Tri/Anual), valor mensal
                             embaixo. Com parcelamento, "ou até 3X" no lugar do mensal.
                             No Basic, Tri/Anual ficam só com o "à vista". -->
                        <div v-else-if="isPriceCell(item)" class="plan-price">
                          <div
                            class="plan-price-total"
                            :class="{ 'plan-price-total--destaque': totalEmDestaque(item) }"
                            v-if="item.months > 1"
                          >
                            <span class="plan-price-valor"
                              >{{ currencyPrefix() }} {{ totalPriceOf(item) }}</span
                            >
                            {{ translate.totalValue }}
                          </div>
                          <div class="sem-juros sem-juros--parcelas" v-if="showInstallments(item)">
                            ou {{ rotuloParcelas(item) }}
                          </div>
                          <div class="plan-price-month" v-else-if="showMonthlyPrice(item)">
                            {{ currencyPrefix() }} {{ monthlyPriceOf(item) }}
                            <span class="plan-permonth">{{
                              translate.perMonth
                            }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <!-- Créditos (Plus) — linha abaixo do valor. -->
              <div
                class="plan-credits pt-2"
                v-if="
                  planTypeName === 'Plus' &&
                  isPeriodEnabled(planTypeName, item.months) &&
                  !item.value_voucher &&
                  item.credits
                "
              >
                <strong>
                  {{ translate.textCredits1 }}
                  {{ creditsForPlus(item) }}
                </strong>
                {{ translate.textCredits3 }} {{ creditsPeriodSuffix(item.months) }}
              </div>

              <!-- Créditos (Gold): ILIMITADOS. O "até 12X" agora fica
                   junto ao valor (linhas acima), não mais nesta linha. Escondido
                   em período bloqueado (igual ao desktop). -->
              <div
                class="plan-credits gold-credits"
                v-if="
                  goldPlan() &&
                  !item.value_voucher &&
                  isPeriodEnabled(planTypeName, item.months)
                "
              >
                <!-- Linha 1: GANHE (destaque) + créditos. Linha 2: de DOWNLOAD ILIMITADOS. -->
                <div class="gold-credits-line1">
                  <span class="plan-credits-ganhe">{{ translate.textCredits1 }}</span>
                  <span class="gold-credits-word">{{ translate.textCreditsWord }}</span>
                </div>
                <div class="gold-credits-line2">{{ translate.textCreditsUnlimited }}</div>
              </div>
            </div>
            <hr
              v-if="item.months < 12 && !isBlockedCell(item) && !hideHrForPromo(item, index)"
              class="hr-options"
            />
          </div>
        </div>

        <!-- Botão de avanço próprio do card: salva ao AVANÇAR (não ao selecionar). -->
        <div
          v-if="hasAnyEnabled"
          class="row d-flex align-items-center justify-content-center"
        >
          <button
            type="button"
            class="plan-button col-lg-3 col-8"
            @click="saveChoicePeriod()"
          >
            {{ isActiveUser ? translate.buttonSubscribe4 : translate.buttonSubscribe3 }}
          </button>
        </div>
        <div
          v-else
          class="row d-flex align-items-center justify-content-center"
        >
          <button type="button" class="plan-button-inactive col-lg-3 col-8">
            {{ isActiveUser ? translate.buttonSubscribe4 : translate.buttonSubscribe3 }}
          </button>
        </div>
      </div>
    </div>

    <!-- Benefícios centralizados abaixo do card, por plano. -->
    <div v-if="props.showBenefits" class="d-flex justify-content-center">
      <div class="col-12 mobile-benefits-wrapper">
        <div
          v-if="getInheritsFromLabel(planTypeName)"
          class="mobile-plan-inherit"
        >
          {{ getInheritsFromLabel(planTypeName) }}
        </div>
        <div
          v-if="getInheritsFromLabel(planTypeName)"
          class="mobile-plan-inherit-plus"
        >
          +
        </div>
        <ul class="mobile-plan-benefits">
          <li
            class="align-items-center"
            v-for="(benefit, i) in getMobileBenefits(planTypeName)"
            :key="i"
          >
            <span class="svg-beneficions" v-html="svgCheck"></span>
            <span class="mobile-plan-benefit-text" v-html="benefit"></span>
          </li>
        </ul>
        <!-- Assinatura sigilosa: rodapé de cada card (mesmo texto do desktop),
             abaixo dos benefícios. -->
        <div class="mobile-plan-secrecy" v-html="translate.secrecyFooter"></div>
      </div>
    </div>

    <!-- Rodapé (limite diário de downloads) só no Gold, como no componente base. -->
    <div v-if="props.showBenefits" class="row">
      <div class="col-lg-12 col-12">
        <div class="footer-list text-center">{{ translate.footerBenefits }}</div>
        <div class="footer-download text-center pb-5" v-if="goldPlan()">
          {{ translate.footerDownloadLimit1 }}
          {{ dailylimit }}
          {{ translate.footerDownloadLimit2 }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="css">
.card-discount-voucher {
  width: 100%;
  height: 42px;
  border-radius: 15px;
  background: #ff8339;
  font-family: "Exo-2";
  font-weight: 700;
  font-size: 20px;
  line-height: 100%;
  letter-spacing: 2%;
  text-align: center;
  vertical-align: middle;
  color: #ffffff;
}

.banner-gold {
  font-family: "Exo-2";
  font-weight: 500;
  font-size: 15px;
  line-height: 100%;
  text-align: center;
  vertical-align: middle;
  color: #ffffff;
  background-color: #000000;
  border-radius: 20px;
  width: 80%;
  margin: -16.5px auto;
  border: none;
}

.card {
  position: relative;
  width: 100%;
  border-radius: 20px;
  margin-top: 60px;
  padding-right: 0px;
}

/* Plus/Basic: borda em degradê top/left #000 → bottom/right #FF6000 (técnica de
   dupla camada: interior branco no padding-box, degradê no border-box). */
.card:not(.card-gold) {
  border: 3px solid transparent;
  background: linear-gradient(#fff, #fff) padding-box,
    linear-gradient(135deg, #000000 0%, #ff6000 100%) border-box;
}

/* Gold: fundo com a imagem (mostrando o centro) e SEM borda. A URL vem inline no
   template (variável --gold-bg, import ES6 do PNG). A imagem fica a 50% de
   opacidade via overlay branco 0.5 por cima dela (o conteúdo do card não é
   afetado, pois é pintado acima do background). */
.card-gold {
  border: none;
  background-color: #fff;
  background-image: linear-gradient(
      rgba(255, 255, 255, 0.5),
      rgba(255, 255, 255, 0.5)
    ),
    var(--gold-bg);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.plantype-name {
  font-family: "Roboto-Medium";
  font-weight: 700;
  font-size: 40px;
  line-height: 100%;
  color: #000000;
  text-align: center;
}

/* Nome "Gold" um pouco maior que Plus/Basic (calibrável). */
.card-gold .plantype-name {
  font-size: 46px;
  /* Centraliza verticalmente o conteúdo do card Gold: a margem automática no topo
     do nome divide o espaço livre igualmente com a margem automática do botão
     (fixado na base pelo wrapper), deixando o vão banner→nome ≈ créditos→botão.
     !important para vencer a utilitária .mt-3 do elemento. */
  margin-top: auto !important;
}

.plantype-desire {
  font-family: "Exo-2";
  font-weight: 400;
  font-size: 12px;
  line-height: 100%;
  color: #4b4b4b;
  text-align: center;
}

.hr-desire {
  margin: 0;
  padding: 0;
  border: none;
  margin-top: 10px;
  border-bottom: solid 1px #5b5858;
  margin-bottom: 15px;
}

.hr-options {
  margin: 0;
  padding: 0;
  border: none;
  border-bottom: solid 1px #5b5858;
  width: 85%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.card-item {
  width: 100%;
  background-color: #fff;
  padding-right: 0px;
}

/* No Gold o fundo do card é a imagem dourada: as faixas de período ficam
   transparentes para a textura aparecer atrás do conteúdo. */
.card-gold .card-item {
  background-color: transparent;
}

/* Períodos habilitados são clicáveis (marcam o rádio via clickRadio): cursor de
   ponteiro ao passar o mouse. A célula bloqueada (.blocked) não é clicável. */
.card-body-item:not(.blocked) {
  cursor: pointer;
}

/* Período BLOQUEADO para upgrade: mesmo visual do `cell-disabled` do desktop —
   fundo cinza, conteúdo centralizado e textos borrados. */
.card-body-item.blocked {
  background: #606060bf;
  border-radius: 5px;
  text-align: center;
  /* 3px de espaço entre células bloqueadas (sem hr entre elas). */
  margin-bottom: 5px;
  opacity: 0.3;
  height: 90px;
}
/* Rádio mantido no DOM (alinha os índices do onMounted) porém oculto. */
.blocked-radio-hidden {
  display: none;
}
/* Texto borrado/desfocado (igual ao `.cell-blur` do desktop). */
.plan-blur {
  filter: blur(1.6px);
  color: #4b4b4b;
  user-select: none;
}
.hr-blur {
  margin: 6px auto;
  padding: 0;
  border: none;
  border-top: 1px solid #9d9d9d;
  width: 50%;
  filter: blur(1.6px);
}

.plan-period {
  font-family: "Exo-2";
  font-weight: 600;
  font-size: 20px;
  line-height: 100%;
  color: #000000;
}

.plan-discount-pct {
  font-family: "Exo-2";
  font-weight: 700;
  font-size: 13px;
  line-height: 100%;
  color: #8C8C8C;
  margin-top: 6px;
}

.plan-price-month {
  font-family: "Exo-2";
  font-weight: 600;
  font-size: 18px;
  line-height: 100%;
  color: #121212;
}

.plan-price-total {
  font-family: "Exo-2";
  font-weight: 400;
  font-size: 13px;
  line-height: 100%;
  color: #707070;
  margin-top: 6px;
}

.plan-permonth {
  font-family: "Exo-2";
  font-weight: 500;
  font-size: 13px;
  line-height: 100%;
  color: #8a8a8a;
}

.plan-balance .use-balance-label {
  font-family: "Exo-2";
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  color: #5b5858;
}

.plan-balance .use-balance-label-plus {
  font-family: "Exo-2";
  font-weight: 900;
  font-size: 16px;
  line-height: 100%;
  color: #5b5858;
}

.plan-balance-value {
  font-family: "Exo-2";
  font-weight: 700;
  font-size: 25px;
  line-height: 100%;
  color: #121212;
  margin-top: 4px;
}

/* Card de UPGRADE em promoção: "de" (preço cheio riscado) sobre "por" (valor a
   pagar). Sem promoção, a linha do "por" tem só o valor — o flex centralizado
   mantém exatamente o visual anterior. O rótulo alinha pela baseline do número. */
.balance-de-row,
.balance-por-row {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 6px;
}
.balance-de-row {
  margin-top: 6px;
}
.balance-de-value {
  font-family: "Exo-2";
  font-weight: 900;
  font-size: 16px;
}
/* Gold: fontes um pouco maiores, como no restante do card. */
.card-gold .balance-de-value {
  font-size: 18px;
}
.card-gold .balance-de-row .plan-depor-label,
.card-gold .balance-por-row .plan-depor-label {
  font-size: 14px;
  color: #000000;
}

.plan-free {
  font-family: "Exo-2";
  font-weight: 700;
  font-size: 20px;
  line-height: 100%;
  color: #ff6000;
}

.plan-credits {
  font-family: "Exo-2";
  font-weight: 400;
  font-size: 11px;
  line-height: 100%;
  color: #000000;
  text-align: center;
}

/* "GANHE" (Gold): em destaque, maior e em linha própria. O restante do texto
   ("Créditos de DOWNLOADS ILIMITADOS") permanece no tamanho atual. */
.plan-credits-ganhe {
  display: block;
  font-weight: 700;
  font-size: 30px;
  line-height: 1.1;
  margin-bottom: 2px;
  margin-top: 50px;
}

/* ----- Card GOLD (visitante/não-ativo, sem promoção): período centralizado e
   empilhado, com fontes maiores que os outros cards. ----- */
.gold-cell {
  width: 100%;
  margin-top: 20px;
}
.gold-line-period {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 10px;  
}
.gold-period {
  font-family: "Exo-2";
  font-weight: 600;
  font-size: 22px; /* > .plan-period (20px) */
  color: #000000;
}
.gold-month {
  font-family: "Exo-2";
  font-weight: 700;
  font-size: 28px; /* > .card-gold .plan-price-month (20px) */
  color: #121212;
  margin-top: 6px;
}
.gold-month .plan-permonth {
  font-size: 12px;
  color: #383737;
}

.plan-blocked .plan-permonth {
  color: #000000;
}

.gold-hr {
  border: none;
  border-top: 1px solid #9d9d9d;
  width: 60%;
  margin: 0px auto;
}
.gold-total {
  font-family: "Exo-2";
  font-weight: 400;
  font-size: 16px; /* > .plan-price-total (13px) */
  color: #383737;
}
.gold-total--destaque {
  margin-top: 6px;
}
.gold-total--destaque .gold-total-valor {
  font-weight: 700;
  font-size: 28px; /* o lugar do valor por mês, que sai com o parcelamento */
  color: #121212;
}
.gold-installments {
  font-family: "Exo-2";
  font-weight: 700;
  font-size: 18px;
  color: #000000;
  margin-top: 2px;
}

/* Créditos do Gold em 2 linhas; "GANHE" inline e grande na linha 1. */
.gold-credits {
  margin-top: 30px;
}
.gold-credits-line1 {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 6px;
}
.gold-credits .plan-credits-ganhe {
  /* sobrepõe o display:block/margin-top atuais (a folga de topo fica no .gold-credits). */
  display: inline;
  margin: 0;
  font-size: 30px;
  line-height: 1;
}
.gold-credits-word {
  font-size: 16px;
  font-weight: 400;
}
.gold-credits-line2 {
  font-size: 18px;
  font-weight: 400;
  margin-top: 2px;
}

.bonus-title {
  font-family: "Exo-2";
  font-weight: 700;
  font-size: 30px;
  line-height: 40px;
  color: #ff6000;
  display: block;
  text-align: center;
}

.sem-juros {
  font-family: "Exo-2";
  font-weight: 600;
  font-size: 10px;
  line-height: 16px;
  color: #6fa923;
}

/* Apenas no card GOLD (mobile): preço/mês, preço à vista e "até 12x SEM JUROS"
   ganham +3px em relação ao tamanho padrão dos demais planos. */
/* Sem o valor por mês, o à vista toma o destaque dele. Com parcelamento, o
   "ou até 3X" vem logo abaixo, em preto. */
.plan-price-total--destaque .plan-price-valor {
  font-weight: 600;
  font-size: 18px;
  color: #121212;
}
/* Sem nada abaixo (Basic Tri/Anual), o total fica centrado na linha. */
.plan-price-total:only-child {
  margin-top: 0;
}
.sem-juros--parcelas {
  font-size: 13px;
  line-height: 18px;
  color: #121212;
}

.card-gold .plan-price-month {
  font-size: 20px;
}
.card-gold .plan-price-total {
  font-size: 13px;
}
.card-gold .sem-juros {
  font-size: 15px;
  color: #000000;
}

.plan-balance .sem-juros {
  top: 10px;
}

/* Percentual da promoção (cupom) abaixo do nome do plano (título do card). Só
   renderiza no Gold: #b20000 no mesmo tamanho da fonte do nome "Gold" (46px). */
.plantype-discount-pct {
  font-family: "Exo-2";
  font-weight: 700;
  font-size: 35px;
  line-height: 1;
  color: #b20000;
  text-align: center;
  text-transform: uppercase;
  margin-top: 4px;
}

/* Promoção / upgrade "de / por": as duas colunas lado a lado (mesma estrutura
   do desktop). Cada coluna = rótulo ("de"/"por") + pilha de valores centralizada. */
.plan-depor-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  width: 100%;
}
.plan-depor-col {
  display: flex;
  align-items: center;
  gap: 4px;
}
.plan-depor-stack {
  text-align: center;
}
.plan-depor-label {
  font-family: "Exo-2";
  font-weight: 500;
  font-size: 12px;
  color: #5b5858;
}
/* "de": valores menores e riscados em vermelho ao centro. */
.plan-depor-col:first-child .plan-price-month {
  font-size: 13px;
  color: #8a8a8a;
  font-weight: 500;
}
.plan-de-strike {
  text-decoration: line-through;
  text-decoration-color: #b20000;
  text-decoration-thickness: 1px;
  color: #b20000;
  font-weight: 900;
}

/* % de desconto acima do nome de cada período em promoção (Plus/Basic). */
.period-promo-pct {
  font-family: "Exo-2";
  font-weight: 700;
  font-size: 20px;
  line-height: 1;
  color: #b20000;
  text-transform: uppercase;
  margin-bottom: 4px;
}

/* Linha do período em promoção (Plus/Basic): 4 colunas — radio (col-1, fora do
   flex) + nome/"de" + "por" + valor à vista/mês. */
.promo-period-row {
  display: flex;
  /* baseline: o "por" fica na mesma linha da PRIMEIRA linha da coluna de valor
     — o total à vista em Tri/Anual ("por 1,10 à vista"). No Mensal, que não tem
     valor cheio, a primeira linha é o próprio valor/mês, então nada muda. */
  align-items: baseline;
  justify-content: space-between;
  /* Espaço mínimo legível entre o "por" e o valor à vista (a sobra da linha é
     absorvida pela coluna do nome — ver .promo-col-name). */
  gap: 4px;
  width: 100%;
}
/* Mensal: não há valor cheio, a coluna de valor tem uma única linha — mantém o
   "por" centralizado com ela, como era antes. */
.promo-period-row--single {
  align-items: center;
}
.promo-col-name {
  min-width: 0;
  text-align: left;
  /* A coluna do nome/"de" absorve TODO o espaço livre da linha: assim o "por"
     é empurrado para junto do valor à vista (separado só pelo gap de 4px),
     em vez de ficar solto no meio pela distribuição do space-between. */
  flex: 1 1 auto;
  /* O nome/"de" continua centralizado verticalmente (só o "por" usa baseline). */
  align-self: center;
  /* Espaço entre o radio (col-1) e o nome do período, como o ps-2 do layout
     não-promocional (evita que fiquem colados no mobile 360px). */
  padding-left: 10px;
}
/* "de" + valor riscado na mesma linha, logo abaixo do nome. */
.promo-de {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-top: 2px;
}
.promo-de .plan-price-total {
  margin-top: 0;
}
.promo-col-por {
  flex: 0 0 auto;
}
/* Coluna 4: valor à vista (em cima) + valor mensal (embaixo), alinhados à direita. */
.promo-col-value {
  flex: 0 0 auto;
}
.promo-col-value .plan-price-total {
  margin-top: 0;
}

/* Fundo destacado dos períodos em promoção (Plus/Basic) — #FFAF7E a 35%.
   Sangra até a borda do card contornando o padding horizontal de 16px do
   .card-body (margem negativa) e devolve o conteúdo ao lugar com padding
   equivalente. margin-top cria os 10px de espaço em branco entre os períodos
   (o HR é removido nesses casos). */
.card-body-item.promo-highlight {
  background: rgba(255, 175, 126, 0.35);
  margin-left: -16px;
  margin-right: -16px;
  padding-left: 16px;
  padding-right: 16px;
  margin-bottom: 10px;
  border-radius: 0;
}

/* GOLD em promoção: layout vertical "de" (total riscado) sobre "por". */
.gold-de-row,
.gold-por-row {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 6px;
  margin-top: 6px;
}
/* "por": o rótulo alinha pela baseline da PRIMEIRA linha de valor, ou seja,
   fica na mesma linha do total à vista (ex.: "por 8,00 à vista"). O baseline
   herdado do bloco acima já garante isso — nada a sobrescrever aqui. */
.gold-de-label,
.gold-por-label {
  font-family: "Exo-2";
  font-weight: 500;
  font-size: 14px;
  color: #000000;
}
.gold-de-value {
  font-family: "Exo-2";
  font-weight: 900;
  font-size: 18px;
}
.gold-por-stack {
  text-align: center;
}
/* Aproxima o valor "à vista" do valor mensal na coluna "por" (Gold promo):
   zera a margem e reduz o leading do número grande. */
.gold-por-stack .gold-total {
  line-height: 1;
}
.gold-por-stack .gold-month {
  margin-top: 0;
  line-height: 1;
}

.plan-button {
  width: 202px;
  height: 46px;
  background-color: #ff6000 !important;
  border-radius: 15px !important;
  font-family: "Exo-2";
  font-weight: 700;
  font-size: 19.8px;
  line-height: 23.76px;
  color: #ffffff;
  margin-top: 30px;
  margin-bottom: 20px;
  text-align: center;
}

/* Gold: botão "Faça parte" preto com texto branco (Plus/Basic seguem laranja). */
.card-gold .plan-button {
  background-color: #000000 !important;
  color: #ffffff;
}

.plan-button-inactive {
  width: 202px;
  height: 46px;
  background-color: #dfdede !important;
  border-radius: 15px !important;
  font-family: "Exo-2";
  font-weight: 700;
  font-size: 19.8px;
  line-height: 23.76px;
  color: #ffffff;
  margin-top: 50px;
  margin-bottom: 20px;
  cursor: not-allowed;
  border: 1px solid #ffffff;
}

.form-check-input:checked[type="radio"] {
  --bs-form-check-bg-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='2' fill='%23000'/%3e%3c/svg%3e");
}

.form-check-input:checked {
  background-color: #ffffff;
  border-color: #000;
}

.form-check-input {
  --bs-form-check-bg: #fff;
  width: 24px;
  height: 24px;
}

/* Radio NÃO marcado nos períodos em promoção (Plus/Basic): fundo peach #FFAF7E
   a 50% de opacidade. Marcado continua branco com o ponto preto. */
.card-body-item.promo-highlight .form-check-input:not(:checked) {
  --bs-form-check-bg: rgba(255, 175, 126, 0);
  background-color: rgba(255, 175, 126, 0);
  border-color: #cccccc;
}

.carimbo-mais-escolhido {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  width: 71px;
  height: 58px;
  transform: rotate(38deg);
}

/* Benefícios mobile (mesmos estilos do AssinePlanCard.vue). O margin-top reserva
   o espaço entre o card e os benefícios onde o CarrosselBella posiciona os dots
   da paginação (ver positionPaginationPeek). */
.mobile-benefits-wrapper {
  padding: 0 8px;
  margin-top: 35px;
}

.mobile-plan-inherit {
  font-family: "Exo-2";
  font-weight: 700;
  font-size: 18px;
  line-height: 22px;
  color: #000000;
  text-align: center;
  margin-top: 20px;
}

.mobile-plan-inherit-plus {
  font-family: "Exo-2";
  font-weight: 700;
  font-size: 22px;
  line-height: 24px;
  color: #000000;
  text-align: center;
  margin-bottom: 8px;
}

.mobile-plan-benefits {
  list-style: none;
  padding: 0;
  margin: 12px 0 16px 0;
}

.mobile-plan-benefits li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 6px;
  font-family: "Exo-2";
  font-weight: 500;
  font-size: 15px;
  line-height: 1.35;
  color: #050505;
  text-align: left;
}

.mobile-plan-benefit-text {
  flex: 1;
}

/* Assinatura sigilosa (rodapé do card, abaixo dos benefícios): mesmo texto do
   desktop, em vermelho. O rótulo negrito vem do <b> do próprio texto (v-html). */
.mobile-plan-secrecy {
  font-family: "Exo-2";
  font-weight: 500;
  font-size: 15px;
  line-height: 1.35;
  color: #b20000;
  text-align: center;
  margin: 44px 0 16px 0;
}

.footer-list {
  color: #5b5858;
  font-family: "Roboto-Regular";
  font-size: 13px;
  font-style: italic;
  letter-spacing: 0;
  line-height: 15px;
  padding-top: 30px;
  text-align: left;
}

.footer-download {
  color: #5b5858;
  font-family: "Roboto-Regular";
  font-size: 13px;
  font-style: italic;
  letter-spacing: 0;
  line-height: 15px;
  text-align: left;
}

/* Efeito carrossel (mesmo padrão do AssinePlanPeriodMobile.vue). A posição e o
   raio ficam por slide; a BORDA segue o novo esquema: Plus/Basic com degradê
   linear #000→#FF6000 e Gold sem borda (mantém o fundo com imagem). */
.carousel__slide--active .card {
  top: -50px;
  border-radius: 25px;
}
.carousel__slide--prev .card {
  left: 30px;
  border-radius: 25px;
}
.carousel__slide--next {
  z-index: -1;
}
.carousel__slide--next .card {
  right: 30px;
  border-radius: 25px;
}

.carousel__slide--active .card:not(.card-gold),
.carousel__slide--prev .card:not(.card-gold),
.carousel__slide--next .card:not(.card-gold) {
  border: solid 3px transparent;
  background: linear-gradient(#ffffff, #ffffff) padding-box,
    linear-gradient(135deg, #000000 0%, #ff6000 100%) border-box;
}

.carousel__slide--active .card-gold,
.carousel__slide--prev .card-gold,
.carousel__slide--next .card-gold {
  border: none;
}

/* Estilos do card no modo carrossel (wide-peek). Antes gateados por
   @media (max-width:769px); agora gateados pela classe ancestral .carousel--peek
   (presente só quando o card está dentro do CarrosselBella em modo peek —
   montado em ≤970px na index/planos e em <770px na Minha Conta). Assim o grid
   desktop nunca recebe estes estilos, em qualquer largura, e o carrossel de 1
   card os mantém até 970px (iPads/tablets). */

/* Linhas de período com altura uniforme (mesmo ajuste do desktop): o Plus tem
   a linha de créditos que aumenta a altura do item; o min-height faz o Basic
   (sem créditos) reservar o mesmo espaço, deixando os períodos simétricos em
   altura entre os cards. Não afeta a célula bloqueada (altura própria). */
.carousel--peek .card-body-item:not(.blocked) {
  min-height: 100px;
  /* Centraliza verticalmente as informações do período dentro do elemento
     (cards sem créditos, como o Basic, deixavam o conteúdo colado no topo,
     muito perto do hr acima). */
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* O card branco ocupa toda a largura do slide (zera o padding do .container
   raiz do Bootstrap). O conteúdo interno continua protegido pelo padding do
   .card-body, então o "10px de margem" e o "10px de gap" definidos no
   CarrosselBella (modo wide-peek) passam a valer para os cards visíveis. */
.carousel--peek .container {
  padding-left: 0;
  padding-right: 0;
}
/* Alturas iguais entre os cards (min-height vem do CarrosselBella via JS):
   o card vira coluna flex e o card-body cresce, empurrando o botão para a
   base. Assim a altura extra dos cards mais baixos vira espaço acima do
   botão, em vez de deixá-lo "flutuando" no meio. */
.carousel--peek .card {
  margin-top: 30px;
  display: flex;
  flex-direction: column;
}
.carousel--peek .card-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
}
.carousel--peek .card-body > .row:last-child {
  margin-top: auto;
}
.carousel--peek .carousel__slide--active {
  padding: 10px;
}
/* Os três cards ficam alinhados pelo topo: o card ativo NÃO "sobe" em relação
   aos vizinhos (sem deslocamento vertical ao trocar de slide). A borda
   gradiente continua destacando o card ativo. */
.carousel--peek .carousel__slide--active .card {
  top: 0;
}
.carousel--peek .carousel__slide--prev .card,
.carousel--peek .carousel__slide--next .card {
  left: unset;
  right: unset;
}
/* Plus/Basic vizinhos no peek mantêm o degradê linear #000→#FF6000; o Gold
   preserva o fundo com imagem (sem borda). */
.carousel--peek .carousel__slide--prev .card:not(.card-gold),
.carousel--peek .carousel__slide--next .card:not(.card-gold) {
  background: linear-gradient(#ffffff, #ffffff) padding-box,
    linear-gradient(135deg, #000000 0%, #ff6000 100%) border-box;
}

/* ===== Tema Gold (aba Renovar/Upgrade da Minha Conta) ===== */
/* A classe .gold-theme vem da raiz do NavBarAccount — em /assine ela não
   existe, então os cards continuam iguais naquela página. Só a BORDA dos
   cards Plus/Basic muda: dourada lisa, sem o degradê #000→#FF6000. */
.gold-theme .card:not(.card-gold) {
  border: 3px solid #e9c554;
  background: #ffffff;
}

/* Mesma borda nos vizinhos do carrossel (mobile), que reaplicam o degradê. */
.gold-theme .carousel--peek .carousel__slide--prev .card:not(.card-gold),
.gold-theme .carousel--peek .carousel__slide--next .card:not(.card-gold) {
  background: #ffffff;
}

/* Benefícios: ficam FORA do card (sobre o fundo escuro da aba), então o texto
   preto do padrão sumia. */
.gold-theme .mobile-plan-benefits li,
.gold-theme .mobile-plan-inherit,
.gold-theme .mobile-plan-inherit-plus {
  color: #ffffff;
}

/* Assinatura sigilosa: dourado no lugar do vermelho. */
.gold-theme .mobile-plan-secrecy {
  color: #e9c554;
}

/* Notas do rodapé: cinza claro, mesmo tom das notas da Minha Conta. */
.gold-theme .footer-list,
.gold-theme .footer-download {
  color: #c2c2c2;
}
</style>
